import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Filter, Search, ArrowLeft, ChevronRight, Shield, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientPlacementCategory() {
  const { categoryId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [showFilters, setShowFilters] = useState(false);
  const [lockModal, setLockModal] = useState(null);
  const [lockCode, setLockCode] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/placements/categories/${categoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/placements/products`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { category_id: categoryId }
        })
      ]);
      
      setCategory(catRes.data);
      const productsData = Array.isArray(prodRes.data)
        ? prodRes.data
        : (prodRes.data.products || []);
      setProducts(productsData.filter(p => p.is_active));
    } catch (error) {
      console.error('Failed to fetch data:', error);
      navigate('/client/placements');
    } finally {
      setLoading(false);
    }
  }, [categoryId, token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredProducts = products
    .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'price') return a.min_investment - b.min_investment;
      if (sortBy === 'rate') return b.rentability_rate - a.rentability_rate;
      return (a.order ?? 0) - (b.order ?? 0);
    });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/client/placements')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white" data-testid="category-title">
              Les placements
            </h1>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex items-center gap-4 bg-card/50 backdrop-blur p-4 rounded-xl border border-border/50">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un placement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-background/50 border-border/50"
              data-testid="search-input"
            />
          </div>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-btn"
          >
            <Filter className="w-4 h-4" />
            Plus de filtre
          </Button>
        </div>

        {/* Filter Tags */}
        <div className="flex flex-wrap gap-2">
          <Badge 
            variant={!category ? 'default' : 'outline'}
            className="cursor-pointer px-4 py-2 text-sm"
            onClick={() => navigate('/client/placements')}
          >
            Investissements
          </Badge>
          {category && (
            <Badge className="bg-blue-500 text-white px-4 py-2 text-sm">
              {category.title}
            </Badge>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <Card 
              key={product.id}
              className="overflow-hidden bg-card/80 backdrop-blur border-border/50 hover:border-blue-500/50 transition-all cursor-pointer group"
              onClick={() => {
                if (product.is_locked) {
                  setLockModal(product);
                  setLockCode('');
                } else {
                  navigate(`/client/placements/product/${product.id}`);
                }
              }}
              data-testid={`product-card-${product.id}`}
            >
              <CardContent className="p-0">
                {/* Category Badge */}
                <div className="px-4 pt-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-xs text-muted-foreground">
                    {category?.title || 'Offres de Placement'}
                  </span>
                </div>

                {/* Product Image */}
                <div className="relative h-44 mx-4 mt-2 rounded-lg overflow-hidden bg-gradient-to-br from-blue-900/50 to-indigo-900/50">
                  {product.images?.[0] ? (
                    <img 
                      src={product.images[0]} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-20 h-20 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <Shield className="w-10 h-10 text-blue-400" />
                      </div>
                    </div>
                  )}
                  
                  {/* Banner Badge */}
                  {product.banner_text && (
                    <Badge 
                      className="absolute top-2 left-2 text-xs"
                      style={{ backgroundColor: product.banner_color || '#22c55e' }}
                    >
                      {product.banner_text}
                    </Badge>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-4 space-y-3">
                  {/* Status & Name */}
                  <div>
                    <Badge variant="outline" className="text-green-400 border-green-400/50 text-[10px] mb-2">
                      • OUVERT
                    </Badge>
                    <h3 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">
                      {product.name}
                    </h3>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-muted-foreground">à partir de</span>
                      <p className="font-bold text-white">
                        {product.min_investment?.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-400">
                        {product.rentability_rate}% 
                        {product.rentability_variable > 0 && ` à ${product.rentability_variable}%`}
                        <span className="text-xs text-muted-foreground ml-1">
                          {product.rentability_period}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="text-xs text-muted-foreground">
                    📅 {product.duration_months} Mois
                  </div>

                  {/* CTA */}
                  <Button 
                    className={`w-full mt-2 ${product.is_locked ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                    data-testid={`discover-product-${product.id}`}
                  >
                    {product.is_locked ? <Lock className="w-4 h-4 mr-2" /> : null}
                    Découvrir
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-full bg-muted/20 flex items-center justify-center mb-4">
              <Search className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Aucun produit trouvé</h3>
            <p className="text-muted-foreground">
              {searchTerm ? 'Essayez une autre recherche.' : 'Aucun produit disponible dans cette catégorie.'}
            </p>
          </div>
        )}

        {/* Bottom Navigation Tabs */}
        <div className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur border-t border-border/50 p-4 lg:left-64">
          <div className="flex justify-center gap-8 max-w-3xl mx-auto">
            {['Investissements', 'Arbitrage', 'Bourse', 'Crypto', 'Mon espace'].map((tab) => (
              <button 
                key={tab}
                className={`text-sm ${tab === 'Investissements' ? 'text-blue-400' : 'text-muted-foreground hover:text-white'} transition-colors`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Spacer for bottom nav */}
        <div className="h-20" />

        {/* Lock Password Modal */}
        <Dialog open={!!lockModal} onOpenChange={() => setLockModal(null)}>
          <DialogContent className="max-w-sm" data-testid="placement-lock-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-500" />
                Accès protégé
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Ce placement nécessite un code d&apos;accès. Contactez votre conseiller pour l&apos;obtenir.
              </p>
              <Input
                type="password"
                value={lockCode}
                onChange={(e) => setLockCode(e.target.value)}
                placeholder="Entrez le code d'accès"
                data-testid="placement-lock-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && lockCode && lockModal) {
                    axios.post(`${API}/clients/verify-placement-password`, {
                      product_id: lockModal.id,
                      password: lockCode
                    }, { headers: { Authorization: `Bearer ${token}` } })
                      .then(() => {
                        setLockModal(null);
                        navigate(`/client/placements/product/${lockModal.id}`);
                      })
                      .catch(() => toast.error('Code incorrect'));
                  }
                }}
              />
              <Button
                className="w-full"
                onClick={async () => {
                  if (!lockCode || !lockModal) return;
                  try {
                    await axios.post(`${API}/clients/verify-placement-password`, {
                      product_id: lockModal.id,
                      password: lockCode
                    }, { headers: { Authorization: `Bearer ${token}` } });
                    setLockModal(null);
                    navigate(`/client/placements/product/${lockModal.id}`);
                  } catch {
                    toast.error('Code incorrect');
                  }
                }}
                data-testid="placement-lock-submit"
              >
                Accéder
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
